/**
 * ProtectedRoute.js
 * Route guard component that handles authentication and role-based access
 * 
 * Usage:
 *   <ProtectedRoute>              - requires any authenticated user (company or admin)
 *   <ProtectedRoute requireAdmin> - requires admin user type
 *   <ProtectedRoute requireCompany>  - requires company user type (positive check)
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

function ProtectedRoute({ children, requireAdmin, requireCompany }) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Show nothing while checking auth status
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ fontSize: 'var(--font-size-base)', color: '#6B7280' }}>Loading...</div>
      </div>
    );
  }

  // Not authenticated at all - redirect to appropriate login
  if (!isAuthenticated) {
    if (requireAdmin) {
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userType = user?.user_type;

  // Admin route protection
  if (requireAdmin && userType !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  // Company route protection - a positive check (Lane 1331): only a company
  // user may open the company shell. Admin users go to their own portal; any
  // other or missing type goes to sign-in (the old supplier portal and its
  // redirect left with Lane 1410 W6).
  if (requireCompany && userType === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  if (requireCompany && userType !== 'company') {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export default ProtectedRoute;
